import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials, stats } from '../../data/testimonials';

const TestimonialsSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  return (
    <section className="section-padding bg-dark-800 relative overflow-hidden">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="section-title text-gradient mb-6">
              What Our Clients Say
            </h2>
            <p className="section-subtitle">
              Don't just take our word for it - hear from the people who have transformed their spaces with our furniture
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div variants={containerVariants} className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center group"
              >
                <div className="text-4xl md:text-5xl font-bold text-accent-400 mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-white font-semibold mb-1">
                  {stat.label}
                </div>
                <div className="text-dark-400 text-sm">
                  {stat.description}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Main Testimonial */}
          <motion.div variants={itemVariants} className="relative">
            <div className="glass-effect rounded-3xl p-8 md:p-12 max-w-4xl mx-auto relative">
              {/* Quote Icon */}
              <div className="absolute top-6 left-6 w-16 h-16 bg-gradient-accent rounded-full flex items-center justify-center">
                <Quote className="h-8 w-8 text-white" />
              </div>

              {/* Testimonial Content */}
              <div className="pt-8 md:pt-4">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  {/* Rating */}
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-xl md:text-2xl text-white leading-relaxed mb-8 font-medium">
                    "{testimonials[currentTestimonial].content}"
                  </blockquote>

                  {/* Author Info */}
                  <div className="flex items-center justify-center space-x-4">
                    <img
                      src={testimonials[currentTestimonial].image}
                      alt={testimonials[currentTestimonial].name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-accent-500"
                    />
                    <div className="text-left">
                      <div className="text-lg font-semibold text-white">
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className="text-accent-400 font-medium">
                        {testimonials[currentTestimonial].role}
                        {testimonials[currentTestimonial].company && (
                          <span className="text-dark-300"> at {testimonials[currentTestimonial].company}</span>
                        )}
                      </div>
                      <div className="text-dark-400 text-sm">
                        {testimonials[currentTestimonial].location} • {testimonials[currentTestimonial].productPurchased}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-center mt-8 space-x-4">
                <button
                  onClick={prevTestimonial}
                  className="w-12 h-12 bg-dark-700 hover:bg-dark-600 rounded-full flex items-center justify-center transition-colors duration-300"
                >
                  <ChevronLeft className="h-6 w-6 text-white" />
                </button>

                {/* Dots Indicator */}
                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentTestimonial(index);
                        setIsAutoPlaying(false);
                      }}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentTestimonial
                          ? 'bg-accent-400 scale-125'
                          : 'bg-dark-600 hover:bg-dark-500'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="w-12 h-12 bg-dark-700 hover:bg-dark-600 rounded-full flex items-center justify-center transition-colors duration-300"
                >
                  <ChevronRight className="h-6 w-6 text-white" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Additional Testimonials Grid */}
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                variants={itemVariants}
                className="testimonial-card group cursor-pointer"
                onClick={() => {
                  setCurrentTestimonial(index);
                  setIsAutoPlaying(false);
                }}
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <p className="text-dark-300 mb-4 leading-relaxed line-clamp-3">
                  "{testimonial.content}"
                </p>
                
                <div className="flex items-center space-x-3">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-white font-medium text-sm">
                      {testimonial.name}
                    </div>
                    <div className="text-dark-400 text-xs">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div variants={itemVariants} className="text-center mt-16">
            <div className="bg-gradient-to-br from-accent-500/20 to-accent-600/20 rounded-3xl p-8 md:p-12 border border-accent-500/30">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to Join Our Happy Clients?
              </h3>
              <p className="text-dark-200 mb-8 max-w-2xl mx-auto">
                Experience the ResinCraft difference for yourself. Let's create something beautiful together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="btn-primary">
                  Start Your Project
                </button>
                <button className="btn-secondary">
                  View Portfolio
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Background Elements */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent-600/5 rounded-full blur-3xl"></div>
    </section>
  );
};

export default TestimonialsSection;